import type { PhotosByEvent } from '../types';
import { parseSeasonId } from './seasonDomain';
import { formatSeasonDisplay } from './seasonHelpers';
import { sortEventsByMonth, sortSeasonsChronologically } from './sortingUtils';
import type { TvOrder } from './tvConfig';

export interface TvSlide {
  id: number;
  caption: string | null;
  eventName: string;
  seasonLabel: string;
}

type RandomSource = () => number;
type EventEntry = PhotosByEvent[string];

function shuffle<T>(items: readonly T[], random: RandomSource): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function slidesForEvent(entry: EventEntry): TvSlide[] {
  const seasonLabel = formatSeasonDisplay(entry.season, String(entry.event.year));
  return [...entry.photos]
    .filter((photo) => photo.mediaType === 'image')
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((photo) => ({ id: photo.id, caption: photo.caption, eventName: entry.event.name, seasonLabel }));
}

interface SeasonGroup {
  name: string;
  year: number;
  entries: EventEntry[];
}

// A malformed season id keeps an empty name (ranked after every real season)
// and falls back to its first event's year.
function groupBySeason(entries: EventEntry[]): SeasonGroup[] {
  const groupsBySeasonId = new Map<string, SeasonGroup>();

  for (const entry of entries) {
    const existingGroup = groupsBySeasonId.get(entry.season);
    if (existingGroup) {
      existingGroup.entries.push(entry);
      continue;
    }

    const parsed = parseSeasonId(entry.season);
    groupsBySeasonId.set(entry.season, {
      name: parsed?.name ?? '',
      year: parsed?.year ?? entry.event.year,
      entries: [entry],
    });
  }

  return [...groupsBySeasonId.values()];
}

/** Entries oldest season first, events in month order within each season. */
function chronological(entries: EventEntry[]): EventEntry[] {
  const orderedEntries: EventEntry[] = [];

  for (const group of sortSeasonsChronologically(groupBySeason(entries))) {
    const entriesByEventId = new Map(group.entries.map((entry) => [entry.event.id, entry]));
    const events = group.entries.map((entry) => entry.event);

    for (const event of sortEventsByMonth(events, group.name)) {
      const entry = entriesByEventId.get(event.id);
      if (entry) {
        orderedEntries.push(entry);
      }
    }
  }

  return orderedEntries;
}

/** Image slides from the library in the requested order: shuffled events, shuffled slides, or chronological. */
export function buildTvPlaylist(
  photosByEvent: PhotosByEvent,
  order: TvOrder,
  random: RandomSource = Math.random
): TvSlide[] {
  const entries = Object.values(photosByEvent);
  switch (order) {
    case 'event': return shuffle(entries, random).flatMap(slidesForEvent);
    case 'random': return shuffle(entries.flatMap(slidesForEvent), random);
    case 'chrono': return chronological(entries).flatMap(slidesForEvent);
  }
}
